<template>
    <section class="section">
        <div class="container">
            <div class="row">
                <div class="col-md-6">
                    <h2 v-html="$t('shop.order_details')"></h2>
                </div>
            </div>

            <div v-if="isCompletedView || (!isPaymentConfirmed && !isSetupCompleted)" class="row order-data-col">
                <div class="col-md-12 mb-2">
                    <div class="bg-light rounded px-4 py-3">
                        <div class="row">
                            <div class="col-md-6 my-1">
                                <span class="text-quote" v-html="$t('generic.product')"></span>
                                <span class="h6 mt-1 d-block">
                                    {{ order.product.name }} -
                                    <template v-if="this.isVoucherOrder">
                                        {{ this.order.option.description }}
                                    </template>
                                    <template v-else-if="this.order.product_type === 'route_toll'">
                                        {{ this.order.option.name }}
                                    </template>
                                    <template v-else>
                                        <template v-if="typeof this.order.option.displayName === 'string' && this.order.option.displayName.length">
                                            <span v-html="this.order.option.displayName"></span>
                                        </template>
                                        <template v-else>
                                            <span v-html="$t(`shop.vehicle_${this.order.option.vehicle_type}`)"></span>
                                        </template>
                                    </template>
                                </span>
                            </div>
                            <template v-if="!isVoucherOrder">
                                <div v-if="order.dispatch_on && !order.was_dispatched" class="col-md-6 my-1">
                                    <span class="text-quote" v-html="$t('shop.dispatch_on')"></span>
                                    <span class="h6 mt-1 d-block">{{ order.dispatch_on_string }}</span>
                                </div>

                                <div class="col-md-6 my-1">
                                    <template v-if="showEstimatedValidity || showEstimatedText">
                                        <span class="text-quote" v-html="$t('shop.estimated_validity_start')"></span>
                                    </template>
                                    <template v-else>
                                        <span class="text-quote" v-html="$t('shop.validity_start')"></span>
                                    </template>
                                    <span class="h6 mt-1 d-block">
                                        <template v-if="showEstimatedValidity">
                                        <span>
                                            {{ order.estimated_validity_start_timezone_string }}
                                            {{ order.validity_end_string ? '- ' + order.validity_end_string : '' }}
                                            <template v-if="order.order_timezone">({{ $t('shop.customer_timezone_description') }})</template>
                                        </span>
                                        <template v-if="order.order_timezone">
                                            <span class="h6 mt-1 d-block">
                                                {{ order.estimated_validity_start_shop_timezone_string }}
                                                {{ validityEndShopTimezoneString ? '- ' + validityEndShopTimezoneString : '' }}
                                                ({{ $t('shop.shop_timezone_description') }})
                                            </span>
                                        </template>
                                    </template>
                                    <template v-else>
                                        <span class="h6 mt-1 d-block">
                                            {{ order.validity_start_string }}
                                            {{ order.validity_end_string ? '- ' + order.validity_end_string : '' }}
                                            <template v-if="order.order_timezone">
                                                ({{ $t('shop.customer_timezone_description') }})
                                            </template>
                                        </span>
                                        <template v-if="order.order_timezone">
                                            <span class="h6 mt-1 d-block">
                                                {{ validityStartShopTimezoneString }}
                                                {{
                                                    validityEndShopTimezoneString
                                                        ? '- ' + validityEndShopTimezoneString
                                                        : ''
                                                }}
                                                ({{ $t('shop.shop_timezone_description') }})
                                            </span>
                                        </template>
                                    </template>
                                    </span>
                                </div>
                                <div class="col-md-6 my-1">
                                    <span class="text-quote" v-html="$t('shop.license_plate_explicit_order')"></span>
                                    <span class="h6 mt-1 d-block">{{ order.license }}</span>
                                </div>
                            </template>

                            <div class="col-md-6 my-1">
                                <span class="text-quote" v-html="$t('attributes.email')"></span>
                                <span class="h6 mt-1 d-block">{{ order.email }}</span>
                            </div>
                            <div class="col-md-6 my-1">
                                <span class="text-quote" v-html="$t('generic.price')"></span>
                                <span class="h6 mt-1 d-block">
                                    <span :class="{'old': giftVoucherPayments.length > 0}">{{ orderPrice.toFixed(2) }} €</span>
                                    <span v-if="giftVoucherPayments.length > 0">{{ (orderPrice - giftVoucherPaymentsAmount).toFixed(2) }} €</span>
                                </span>
                                <small class="ml-3 d-block text-success" v-for="payment in giftVoucherPayments" :key="payment.id">
                                    {{ $t('giftVoucher.infoAmountCredited', {amount: payment.amount.toFixed(2)}) }}
                                </small>
                            </div>

                            <div v-if="isSetupCompleted && order.externalProductId !== null" class="col-md-6 my-1">
                                <span class="text-quote" v-html="$t('order.externalProductId')"></span>
                                <span class="h6 mt-1 d-block">{{ order.externalProductId }}</span>
                            </div>
                            <div class="col-md-6 my-1" v-if="!isVoucherOrder">
                                <span class="text-quote" v-html="$t('order.registered-status')"></span>
                                <span
                                    class="h6 mt-1 d-block"
                                    :class="{
                                        'text-danger': !successfulCompleted,
                                        'text-success': successfulCompleted,
                                    }"
                                    v-html="$t(`generic.${successfulCompleted ? 'yes' : 'no'}`)"
                                ></span>
                            </div>

                            <div class="col-md-6 my-1">
                                <span class="text-quote" v-html="$t('order.payed')"></span>
                                <span
                                    class="h6 mt-1 d-block"
                                    :class="{
                                        'text-danger': !order.is_fully_paid,
                                        'text-success': order.is_fully_paid,
                                    }"
                                    v-html="$t(`generic.${order.is_fully_paid ? 'yes' : 'no'}`)"
                                ></span>
                            </div>

                            <div
                                v-if="showInsurance"
                                :class="
                                    hasInsurance && order.dispatch_on && isPaymentConfirmed
                                        ? 'col-md-12 my-1'
                                        : 'col-md-6 my-1'
                                "
                            >
                                <span class="text-quote" v-html="$t('order.insuranceTitle')"></span>
                                <span class="h6 mt-1 d-block">
                                    <span
                                        :class="{
                                            'text-danger': !hasInsurance,
                                            'text-success': hasInsurance,
                                        }"
                                        v-html="$t(`generic.${hasInsurance ? 'yes' : 'no'}`)"
                                    ></span>
                                    <a
                                        v-if="!isCompletedView || !isPaymentConfirmed"
                                        class="btn btn-sm"
                                        :class="{
                                            'btn-outline-danger': hasInsurance,
                                            'btn-primary': !hasInsurance,
                                        }"
                                        @click="$emit('show-insurance-popup')"
                                    >
                                        <span
                                            v-html="$t(`shop.insurance_${hasInsurance ? 'deactivate' : 'activate'}`)"
                                        ></span>
                                    </a>
                                </span>
                            </div>

                            <div v-if="(isFlaggedAsAutoRenew || baseOrderActiveSubscription) && subscriptionLicense" class="col-md-6 my-1">
                                <span class="text-quote" v-html="$t('order.subsciptionLicensePlateTitle')"></span>
                                <span class="h6 mt-1 d-block">
                                    {{ subscriptionLicense }}
                                </span>
                            </div>

                            <template
                                v-if="
                                    order.product_type !== 'route_toll' &&
                                    ((order.order_insurance && !order.was_dispatched && order.dispatch_on) ||
                                        order.order_insurance_light) &&
                                    isPaymentConfirmed &&
                                    !order.was_successful &&
                                    !order.product.insurance_included
                                "
                            >
                                <div class="col-md-6 my-1">
                                    <span class="text-quote" v-html="$t('order.insuranceAbleUntilTitle')"></span>
                                    <span class="h6 mt-1 d-block">{{ order.insuranceApplyAbleFormatted }}</span>
                                </div>

                                <div class="col-md-6 my-1">
                                    <span class="text-quote" v-html="$t('order.cancelOrder')"></span>
                                    <a
                                        v-if="!order.was_dispatched"
                                        class="orderCancellationButton"
                                        @click="$emit('show-cancellation-popup')"
                                    >
                                        <span class="h6 mt-1 d-block" v-html="$t('order.cancel_button')"></span>
                                    </a>
                                    <a v-else class="orderCancellationButton">
                                        <a :href="this.contactUrl" target="_blank"
                                            ><span class="h6 mt-1 d-block" v-html="$t('order.contactSupport')"></span
                                        ></a>
                                    </a>
                                </div>
                            </template>

                            <template
                                v-if="
                                    isPaymentConfirmed &&
                                    (order.product.insurance_included || order.product_type === 'route_toll')
                                "
                            >
                                <div class="col-md-6 my-1">
                                    <span v-if="order.dispatch_on && !order.was_dispatched" class="text-quote" v-html="$t('order.insuranceAbleUntilTitle')"></span>
                                    <span v-else class="text-quote" v-html="$t('order.includedInsuranceAbleUntilTitle')"></span>
                                    <span class="h6 mt-1 d-block">{{ order.insuranceApplyAbleFormatted }}</span>
                                </div>

                                <div class="col-md-6 my-1">
                                    <span class="text-quote" v-html="$t('order.cancelOrder')"></span>
                                    <a v-if="order.dispatch_on && !order.was_dispatched" class="orderCancellationButton" @click="$emit('show-cancellation-popup')">
                                        <span class="h6 mt-1 d-block" v-html="$t('order.cancel_button')"></span>
                                    </a>
                                    <a v-else class="orderCancellationButton">
                                        <a :href="this.contactUrl" target="_blank"><span class="h6 mt-1 d-block" v-html="$t('order.contactSupport')"></span></a>
                                    </a>
                                </div>
                            </template>

                            <!-- Order Action Buttons -->
                            <div class="col-12">
                                <!-- Cancel Order Button -->
                                <div class="row no-gutters" v-if="!isCompletedView && cancelOrderButtonConfig.visible">
                                    <div v-if="cancelOrderButtonConfig.showMessage" class="col-12 p-1">
                                        <div
                                            class="btn btn-block btn-no-hover"
                                            :class="{
                                                'mb-3': !cancelOrderButtonConfig.hideButton,
                                                'btn-danger': cancelOrderButtonConfig.failed,
                                                'btn-primary': !cancelOrderButtonConfig.failed,
                                            }"
                                            v-html="cancelOrderButtonConfig.message"
                                        ></div>
                                    </div>

                                    <div v-if="!cancelOrderButtonConfig.hideButton" class="col-12 p-1">
                                        <button
                                            type="button"
                                            class="btn btn-danger btn-block"
                                            data-toggle="modal"
                                            data-target="#cancel-order-modal"
                                            v-html="$t('order.cancel_button')"
                                        ></button>
                                    </div>

                                    <div
                                        v-if="!cancelOrderButtonConfig.hideButton"
                                        id="cancel-order-modal"
                                        class="modal"
                                        tabindex="-1"
                                    >
                                        <div class="modal-dialog modal-dialog-centered">
                                            <div class="modal-content">
                                                <div class="modal-body">
                                                    <div
                                                        class="btn btn-danger btn-block btn-no-hover"
                                                        v-html="$t('order_cancel.confirmation_text')"
                                                    ></div>
                                                </div>
                                                <div class="modal-footer border-top-0 pt-0">
                                                    <button
                                                        type="button"
                                                        class="btn btn-danger"
                                                        data-dismiss="modal"
                                                        v-html="$t('cancel_order.yes')"
                                                        @click="$emit('cancel-button-clicked')"
                                                    ></button>
                                                    <button
                                                        type="button"
                                                        class="btn btn-outline-primary"
                                                        data-dismiss="modal"
                                                        v-html="$t('cancel_order.no')"
                                                    ></button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <loading-animation :visible="showLoadingAnimation"></loading-animation>
                                </div>

                                <!-- Change Subscription Buttons -->
                                <template v-if="isFlaggedAsAutoRenew || (order.subscription_created && baseOrderActiveSubscription)">
                                    <div class="row row-cols-1 row-cols-md-2 no-gutters">
                                        <div class="col p-1">
                                            <button
                                                type="button"
                                                class="btn btn-block btn-outline-primary"
                                                @click.prevent="onTerminatePaymentSubscription"
                                                v-html="$t('order.terminateSubscriptionButton')"
                                            ></button>
                                        </div>
                                        <div class="col p-1">
                                            <button
                                                type="button"
                                                class="btn btn-block btn-outline-primary"
                                                @click.prevent="onChangeLicenseSubscription"
                                                v-html="$t('order.subscriptionChangePlateButton')"
                                            ></button>
                                        </div>
                                    </div>

                                    <modal
                                        id="terminate-payment-subscription-modal"
                                        size="modal-lg"
                                        :centered="true"
                                        :title="$t('order.terminateSubscriptionTitle')"
                                        :show="terminateSubscriptionModal.show"
                                        :close="terminateSubscriptionModal.close"
                                        :closeable="terminateSubscriptionModal.closable"
                                        @modal-close="
                                            terminateSubscriptionModal.show = false;
                                            terminateSubscriptionModal.close = false;
                                        "
                                        @modal-opening="modalIsVisible = true"
                                        @modal-invisible="modalIsVisible = false"
                                        @modal-visible="onModalVisible"
                                    >
                                        <form
                                            method="post"
                                            :action="orderTerminateSubscriptionUrl"
                                            enctype="multipart/form-data"
                                            @submit.prevent="onTerminatePaymentSubscriptionSubmit"
                                        >
                                            <div class="modal-body pt-0">
                                                <div class="row">
                                                    <div
                                                        v-if="
                                                            terminateSubscriptionResponse.show &&
                                                            typeof terminateSubscriptionResponse.message === 'string' &&
                                                            terminateSubscriptionResponse.message.length > 0
                                                        "
                                                        class="col-12"
                                                    >
                                                        <div
                                                            class="alert"
                                                            :class="{
                                                                'alert-success': terminateSubscriptionResponse.success,
                                                                'alert-danger': !terminateSubscriptionResponse.success,
                                                            }"
                                                        >
                                                            <p class="mb-0">{{ terminateSubscriptionResponse.message }}</p>
                                                        </div>
                                                    </div>

                                                    <div v-if="terminateSubscriptionModal.showForm" class="col-12">
                                                        <div class="row">
                                                            <div class="col-12">
                                                                <p v-html="$t('order.terminateSubscriptionText')"></p>
                                                            </div>

                                                            <div class="col-12">
                                                                <div
                                                                    class="d-flex align-items-center justify-content-start h-checkbox"
                                                                >
                                                                    <div>
                                                                        <input
                                                                            type="checkbox"
                                                                            id="payment_subscription"
                                                                            name="confirmation"
                                                                            required
                                                                            class="checkbox"
                                                                            v-model="terminateSubscriptionForm.confirmation"
                                                                        />
                                                                    </div>
                                                                    <label
                                                                        for="payment_subscription"
                                                                        class="ml-3"
                                                                        v-html="
                                                                            $t('order.terminateSubscriptionConfirmCheckbox')
                                                                        "
                                                                    ></label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="modal-footer justify-content-end bg-light">
                                                <button
                                                    v-if="terminateSubscriptionModal.showForm"
                                                    type="button"
                                                    class="btn btn-secondary"
                                                    @click.prevent="onAbortTerminatePaymentSubscription"
                                                    v-html="$t('generic.cancel')"
                                                    :disabled="terminateSubscriptionOngoing"
                                                ></button>
                                                <button
                                                    type="submit"
                                                    class="btn btn-primary"
                                                    :disabled="terminateSubscriptionOngoing"
                                                >
                                                    <i
                                                        v-if="terminateSubscriptionOngoing"
                                                        class="mdi mdi-loading mdi-spin"
                                                    ></i>
                                                    <span
                                                        v-else-if="!terminateSubscriptionModal.showForm"
                                                        v-html="$t('frontend.close')"
                                                    ></span>
                                                    <span v-else v-html="$t('order.terminateSubscriptionConfirm')"></span>
                                                </button>
                                            </div>
                                        </form>

                                        <loading-animation
                                            :visible="terminateSubscriptionOngoing"
                                            :always-show="false"
                                            preloader-container-id=""
                                            status-container-id=""
                                            :loading-text="$t('order.terminationOngoing')"
                                        ></loading-animation>
                                    </modal>

                                    <modal
                                        id="change-license-subscription-modal"
                                        size="modal-lg"
                                        :centered="true"
                                        :title="$t('order.subsciptionLicensePlateTitle')"
                                        :show="changeLicenseSubscriptionModal.show"
                                        :close="changeLicenseSubscriptionModal.close"
                                        :closeable="changeLicenseSubscriptionModal.closable"
                                        @modal-close="
                                            changeLicenseSubscriptionModal.show = false;
                                            changeLicenseSubscriptionModal.close = false;
                                        "
                                        @modal-opening="modalIsVisible = true"
                                        @modal-invisible="modalIsVisible = false"
                                        @modal-visible="onModalVisible"
                                    >
                                        <form
                                            method="post"
                                            :action="changeLicenseSubscriptionUrl"
                                            enctype="multipart/form-data"
                                            @submit.prevent="onChangeLicenseSubscriptionSubmit"
                                        >
                                            <div class="modal-body">
                                                <div class="row">
                                                    <div
                                                        v-if="
                                                            changeLicenseSubscriptionResponse.show &&
                                                            typeof changeLicenseSubscriptionResponse.message === 'string' &&
                                                            changeLicenseSubscriptionResponse.message.length > 0
                                                        "
                                                        class="col-12"
                                                    >
                                                        <div
                                                            class="alert"
                                                            :class="{
                                                                'alert-success': changeLicenseSubscriptionResponse.success,
                                                                'alert-danger': !changeLicenseSubscriptionResponse.success,
                                                            }"
                                                        >
                                                            <p class="mb-0">
                                                                {{ changeLicenseSubscriptionResponse.message }}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div class="col-12">
                                                        <div class="row">
                                                            <div class="col-12 mb-3">
                                                                <label for="evidenceCountry" class="col-form-label">{{
                                                                    $t('shop.licensing_state')
                                                                }}</label>
                                                                <select
                                                                    id="evidenceCountry"
                                                                    class="form-control height-auto-override"
                                                                    name="country_code"
                                                                    v-model="changeLicenseSubscriptionForm.country_code"
                                                                    required
                                                                >
                                                                    <option :value="false" disabled>
                                                                        {{ $t('shop.dropdown_required') }}
                                                                    </option>
                                                                    <option
                                                                        v-for="option in evidenceConfig.licensePlateCountries"
                                                                        :value="option.country_code"
                                                                        :key="option.country_code"
                                                                    >
                                                                        {{ $t('country.' + option.country_code) }}
                                                                    </option>
                                                                </select>
                                                            </div>

                                                            <div
                                                                v-if="selectedLicensePlateCountry !== null"
                                                                class="col-12 col-md-6 mb-3 mb-md-0"
                                                            >
                                                                <label class="col-form-label">{{
                                                                    $t('shop.license_plate')
                                                                }}</label>
                                                                <license-plate-input
                                                                    :license-plate-country="selectedLicensePlateCountry"
                                                                    :template="selectedLicensePlateCountry.template"
                                                                    :required="true"
                                                                    name="license"
                                                                    id="licensePlate"
                                                                    v-model="changeLicenseSubscriptionForm.license"
                                                                >
                                                                </license-plate-input>
                                                            </div>
                                                            <div
                                                                v-if="selectedLicensePlateCountry !== null"
                                                                class="col-12 col-md-6"
                                                            >
                                                                <label class="col-form-label">{{
                                                                    $t('shop.license_plate_confirm')
                                                                }}</label>
                                                                <license-plate-input
                                                                    :license-plate-country="selectedLicensePlateCountry"
                                                                    :template="selectedLicensePlateCountry.template"
                                                                    :required="true"
                                                                    name="license_confirmation"
                                                                    id="licensePlateConfirm"
                                                                    v-model="
                                                                        changeLicenseSubscriptionForm.license_confirmation
                                                                    "
                                                                >
                                                                </license-plate-input>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="modal-footer bg-light justify-content-end">
                                                <button type="submit" class="btn btn-primary" :disabled="runningRequest">
                                                    <i v-if="runningRequest" class="mdi mdi-loading mdi-spin"></i>
                                                    <span v-else v-html="$t('subscriptionLicensePopup.confirm')"></span>
                                                </button>
                                            </div>
                                        </form>
                                    </modal>
                                </template>

                                <!-- New Change Buttons-->
                                <template v-if="order.system_guest_order">
                                    <div class="row">
                                        <div class="col-12">
                                            <p class="text-muted text-sm">
                                                <i class="mdi mdi-information-outline"></i>
                                                {{ $t('orderChange.notChangableGuestOrder') }}
                                            </p>
                                        </div>
                                    </div>
                                </template>
                                <template v-else>
                                    <order-change-buttons
                                        v-if="!isPaymentRefunded && evidenceConfig !== null"
                                        :ongoing-order-update="ongoingOrderUpdate"
                                        :is-transfer-of-product-enabled="isTransferOfProductEnabled"
                                        :order="order"
                                        :license-plate-countries="evidenceConfig.licensePlateCountries"
                                        :default-license-plate-country="evidenceConfig.defaultLicensePlateCountry"
                                        :default-license-plate="order.license"
                                        :disable-change-buttons="disableChangeButtons"
                                        @order-updated="$emit('order-updated', $event)"
                                        class="mb-2"
                                    ></order-change-buttons>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import OrderChangeButtons from './ChangeButtons.vue';

export default {
    components: { OrderChangeButtons },
    props: {
        order: {
            type: Object,
            required: true,
            default() {
                return {
                    product_type: null,
                    dispatch_on: null,
                    was_dispatched: null,
                    was_successful: null,
                    validity_start_string: null,
                    validity_end_string: null,
                    externalProductId: null,
                    order_insurance: null,
                };
            },
        },
        validityStartShopTimezoneString: {
            type: String,
            required: true,
        },
        validityEndShopTimezoneString: {
            type: String,
            required: true,
        },
        disableChangeButtons: {
            type: Boolean,
            required: true,
        },
        orderPrice: {
            type: Number,
            required: true,
        },
        unpaidOrderPrice: {
            type: Number,
            required: true,
        },
        isPaymentConfirmed: {
            type: Boolean,
            required: true,
            default: false,
        },
        isSetupCompleted: {
            type: Boolean,
            required: true,
        },
        isPaymentRefunded: {
            type: Boolean,
            required: true,
        },
        showLoadingAnimation: {
            type: Boolean,
            required: true,
        },
        isCompletedView: {
            type: Boolean,
            required: true,
        },
        successfulCompleted: {
            type: Boolean,
            required: true,
        },
        baseOrderActiveSubscription: {
            type: Boolean,
            required: true,
        },
        subscriptionLicense: {
            type: String,
            required: true,
        },
        cancelOrderButtonConfig: {
            type: Object,
            required: false,
            default() {
                return {
                    visible: false,
                    hideButton: false,
                    showMessage: false,
                    failed: false,
                    message: null,
                };
            },
        },
        insuranceConfig: {
            type: Object,
            required: false,
        },
        isTransferOfProductEnabled: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
        ongoingOrderUpdate: {
            type: Object,
            required: false,
            default() {
                return null;
            },
        },
        evidenceConfig: {
            type: Object,
            required: false,
            default() {
                return null;
            },
        },
        orderTerminateSubscriptionUrl: {
            type: String,
            required: false,
            default() {
                return null;
            },
        },
        changeLicenseSubscriptionUrl: {
            type: String,
            required: false,
            default() {
                return null;
            },
        },
        hasPaymentSubscription: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
        contactUrl: {
            type: String,
            required: false,
        },
    },

    inject: ['isVoucherOrder', 'giftVoucherPayments'],

    data() {
        return {
            terminateSubscriptionForm: {
                confirmation: false,
            },
            terminateSubscriptionModal: {
                show: false,
                close: false,
                closable: true,
                showForm: true,
            },
            terminateSubscriptionResponse: {
                show: false,
                success: false,
                message: null,
            },
            changeLicenseSubscriptionForm: {
                license: this.order.subscription_license ?? this.order.license,
                license_confirmation: this.order.subscription_license ?? this.order.license,
                country_code: this.order.subscription_country_code ?? this.order.country_code,
            },
            changeLicenseSubscriptionModal: {
                show: false,
                close: false,
                closable: true,
                showForm: true,
            },
            changeLicenseSubscriptionResponse: {
                show: false,
                success: false,
                message: null,
            },
            terminateSubscriptionOngoing: false,
            changeLicenseSubscriptionOngoing: false,
            modalIsVisible: false,
            runningRequest: false,
            confirmError: false,
        };
    },

    computed: {
        /**
         * Determine if the insurance option should be shown.
         *
         * @returns {boolean}
         */
        showInsurance() {
            return !this.isPaymentConfirmed && !this.isSetupCompleted && !this.order.was_dispatched && this.insuranceConfig.canBeOrdered;
        },

        hasInsurance() {
            return this.order.order_insurance || this.order.order_insurance_light;
        },

        /**
         * Determine if the order is flagged as auto-renewable.
         *
         * @returns {boolean}
         */
        isFlaggedAsAutoRenew() {
            return (typeof this.order.auto_renew === 'boolean' && this.order.auto_renew) || this.hasPaymentSubscription;
        },

        /**
         * Search the selected license plate country in the provided option list.
         *
         * @returns {{template:null}|null}
         * */
        selectedLicensePlateCountry() {
            try {
                return this.evidenceConfig.licensePlateCountries.find((country) => {
                    return country.country_code === this.changeLicenseSubscriptionForm.country_code;
                });
            } catch (error) {
                return null;
            }
        },

        /**
         * Determine if the selected license plate country has a template for input.
         *
         * @returns {Boolean}
         * */
        selectedLicensePlateCountryHasTemplate() {
            return (
                this.selectedLicensePlateCountry !== null &&
                typeof this.selectedLicensePlateCountry.template === 'object' &&
                this.selectedLicensePlateCountry.template !== null
            );
        },

        /**
         * Determine if the estimated validity should be shown.
         *
         * @returns {boolean}
         */
        showEstimatedValidity() {
            return !this.isPaymentConfirmed &&
                !this.order.was_successful &&
                !(new Date(this.order.validity_start) > new Date());
        },

        showEstimatedText() {
            return !this.isPaymentConfirmed &&
                !this.order.was_successful &&
                !this.isToday(new Date(this.order.validity_start));
        },

        giftVoucherPaymentsAmount() {
            return this.giftVoucherPayments.reduce((a, b) => a + b.amount, 0);
        },
    },

    methods: {
        /**
         * @param date
         * @returns {boolean}
         */
        isToday(date) {
            const today = new Date();

            return (
                today.getFullYear() === date.getFullYear() &&
                today.getMonth() === date.getMonth() &&
                today.getDate() === date.getDate()
            );
        },

        /**
         * Trigger the visibility of the terminate payment subscription modal.
         *
         * @returns {void}
         */
        onTerminatePaymentSubscription() {
            this.terminateSubscriptionModal.show = true;
            this.terminateSubscriptionModal.close = false;
            this.terminateSubscriptionModal.closable = true;
            this.terminateSubscriptionModal.showForm = true;
        },

        /**
         * Handle the abort callback of the payment subscription termination modal.
         *
         * @returns {void}
         */
        onAbortTerminatePaymentSubscription() {
            this.terminateSubscriptionModal.closable = !this.terminateSubscriptionOngoing;
            this.terminateSubscriptionModal.show = false;
            this.terminateSubscriptionModal.close = true;
            this.terminateSubscriptionModal.showForm = true;
        },

        /**
         * Send the request to cancel the order subscription.
         *
         * @returns {void}
         */
        onTerminatePaymentSubscriptionSubmit() {
            // A request is already ongoing, then restrict the access to the submit until the previous
            // request was completed.
            if (this.terminateSubscriptionOngoing) {
                return;
            }

            // If the form isn't visible reload the page, on confirmation.
            if (!this.terminateSubscriptionModal.showForm) {
                window.location.reload();

                return;
            }

            // Reset the error message of the form.
            this.terminateSubscriptionResponse.show = false;
            this.terminateSubscriptionResponse.message = null;
            this.terminateSubscriptionResponse.success = false;
            this.terminateSubscriptionModal.closable = true;

            // In the case the checkbox wasn't checked, then cancel the submit of the form.
            if (!this.terminateSubscriptionForm.confirmation) {
                this.terminateSubscriptionResponse.message = this.$t('order.pleaseConfirmSubscriptionTerminate');
                this.terminateSubscriptionResponse.success = false;
                this.terminateSubscriptionResponse.show = true;

                return;
            }

            this.terminateSubscriptionOngoing = true;
            this.terminateSubscriptionModal.closable = false;

            axios
                .post(this.orderTerminateSubscriptionUrl, this.terminateSubscriptionForm)
                .then((response) => {
                    this.terminateSubscriptionResponse.message = response.data.message;
                    this.terminateSubscriptionResponse.success = response.data.success;
                    this.terminateSubscriptionResponse.show = true;
                    this.terminateSubscriptionModal.showForm = !this.terminateSubscriptionResponse.success;
                    this.terminateSubscriptionModal.closable = true;
                    this.terminateSubscriptionOngoing = false;

                    setTimeout(function () {
                        window.location.reload();
                    }, 15);
                })
                .catch(() => {
                    this.terminateSubscriptionResponse.message = this.$t('order.subscriptionTerminateFailed');
                    this.terminateSubscriptionResponse.show = true;
                    this.terminateSubscriptionResponse.success = false;
                    this.terminateSubscriptionOngoing = false;
                });
        },

        /**
         * Trigger the visibility of the change license subscription modal.
         *
         * @returns {void}
         */
        onChangeLicenseSubscription() {
            this.changeLicenseSubscriptionModal.show = true;
            this.changeLicenseSubscriptionModal.close = false;
            this.changeLicenseSubscriptionModal.closable = true;
            this.changeLicenseSubscriptionModal.showForm = true;
        },

        /**
         * Handle the abort callback of the payment subscription termination modal.
         *
         * @returns {void}
         */
        onAbortChangeLicenseSubscription() {
            this.changeLicenseSubscriptionModal.closable = !this.terminateSubscriptionOngoing;
            this.changeLicenseSubscriptionModal.show = false;
            this.changeLicenseSubscriptionModal.close = true;
            this.changeLicenseSubscriptionModal.showForm = true;
        },

        /**
         * Send the request to cancel the order subscription.
         *
         * @returns {void}
         */
        onChangeLicenseSubscriptionSubmit() {
            // A request is already ongoing, then restrict the access to the submit until the previous
            // request was completed.
            if (this.changeLicenseSubscriptionOngoing) {
                return;
            }

            // Reset the error message of the form.
            this.changeLicenseSubscriptionResponse.show = false;
            this.changeLicenseSubscriptionResponse.message = null;
            this.changeLicenseSubscriptionResponse.success = false;
            this.changeLicenseSubscriptionModal.closable = true;

            this.changeLicenseSubscriptionOngoing = true;
            this.changeLicenseSubscriptionModal.closable = false;

            axios
                .post(this.changeLicenseSubscriptionUrl, this.changeLicenseSubscriptionForm)
                .then((response) => {
                    this.changeLicenseSubscriptionResponse.message = response.data.message;
                    this.changeLicenseSubscriptionResponse.success = response.data.success;
                    this.changeLicenseSubscriptionResponse.show = true;
                    this.changeLicenseSubscriptionModal.showForm = !this.changeLicenseSubscriptionResponse.success;
                    this.changeLicenseSubscriptionModal.closable = true;
                    this.changeLicenseSubscriptionOngoing = false;

                    if (response.data.success === true) {
                        setTimeout(function () {
                            window.location.reload();
                        }, 15);
                    }
                })
                .catch(() => {
                    this.changeLicenseSubscriptionResponse.message = this.$t('order.subscriptionLicenseChangeFailed');
                    this.changeLicenseSubscriptionResponse.show = true;
                    this.changeLicenseSubscriptionResponse.success = false;
                    this.changeLicenseSubscriptionOngoing = false;
                });
        },

        /**
         * Handle the event callback if a modal is visible.
         *
         * @returns {void}
         */
        onModalVisible() {},
    },
};
</script>
<style lang="scss">
.orderCancellationButton {
    cursor: pointer;
}

#terminate-payment-subscription-modal {
    .loader-container {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 9999999;

        background: rgba(255, 255, 255, 0.8);
    }
    .loader-container .preloader .status {
        .spinner {
            position: relative;
            text-align: center;

            .spinner-inner {
                position: relative;
                margin: auto auto 1rem;

                .double-bounce1,
                .double-bounce2 {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;

                    background-color: #2f55d4;
                    opacity: 0.6;

                    -webkit-animation: sk-bounce 2s infinite ease-in-out;
                    animation: sk-bounce 2s infinite ease-in-out;
                }

                .double-bounce2 {
                    -webkit-animation-delay: -1s;
                    animation-delay: -1s;
                }

                width: 2.5rem;
                height: 2.5rem;
            }

            .spinner-text {
                font-weight: bolder;
            }
        }

        position: absolute;
        top: 50%;
        left: 0;
        right: 0;
        -webkit-transform: translateY(-50%);
        transform: translateY(-50%);
    }
}

span.old {
    text-decoration: line-through;
}
</style>
